<template>
    <DefaultTemplate :model="model">
        <div class="container">
            <div class="mt-12 mb-8">
                <Breadcrumbs :breadcrumbs="model.breadcrumbs" className="text-grey-400" />
            </div>

            <div class="block lg:flex">
                <div class="w-full lg:w-3/4 pr-0 lg:pr-4">
                    <div v-if="model.fagfellevurdert || model.redaksjonsomtale || model.forsideKategori" class="flex flex-wrap space-x-2 justify-end lg:hidden mb-6 text-right">
                        <span v-if="model.forsideKategori" class="text-xs font-bold tracking-wider p-1 text-white rounded-sm" :class="[categoryColor ? `bg-${categoryColor}-600` : 'bg-green-600']">
                            {{ model.forsideKategori }}
                        </span>
                        <span v-if="model.redaksjonsomtale" class="text-xs font-bold tracking-wider bg-gray-500 p-1 text-white rounded-sm">
                            Tilrettelagt av redaksjonen
                        </span>
                        <span class="text-xs font-bold tracking-wider p-1 text-white rounded-sm" :class="[categoryColor ? `bg-${categoryColor}-600` : 'bg-green-600']">
                            Fagfellevurdert artikkel
                        </span>
                    </div>
                    <div v-if="(model.images && model.images.length > 0) || inEditMode">
                        <ImageSlider :images="model.images" :params="imageParams"/>
                    </div>
                    <article :class="[themeColor ? `border-${themeColor}-500` : 'border-white']"
                             class="p-4 md:px-10 md:py-4 lg:px-20 lg:py-10 mb-4 bg-white border-t-10">
                        <h4 v-epi-edit="'Name'" class="mb-2">
                            {{ model.name }}
                        </h4>
                        <h1 v-if="model.heading || inEditMode"
                            v-epi-edit="'Heading'"
                            class="mb-2">
                            {{ model.heading }}
                        </h1>
                        <div v-if="model.intro || inEditMode" v-epi-edit="'Intro'" class="main-body my-4 article pt-4" v-html="model.intro" />
                        <ContentArea v-epi-edit="'IntroContent'" :model="model.introContent" />
                        <div v-if="model.mainBody || inEditMode" v-epi-edit="'MainBody'" id="main-body" class="main-body my-4 article pt-4" v-html="model.mainBody" />
                        <ContentArea v-epi-edit="'MainContent'" :model="model.mainContent" />
                        <div v-if="model.secondaryBody || inEditMode" v-epi-edit="'SecondaryBody'" id="seconday-body" class="main-body my-4 article" v-html="this.model.secondaryBody" />
                    </article>
                </div>
                <aside class="w-full lg:w-1/4 pr-0 lg:pl-4 mb-4">
                    <div v-if="model.fagfellevurdert || model.redaksjonsomtale || model.forsideKategori" class="hidden lg:flex lg:flex-wrap lg:justify-end lg:space-x-2 mb-4 text-right">
                        <span v-if="model.forsideKategori" class="text-xs font-bold tracking-wider p-1 text-white rounded-sm mb-2" :class="[categoryColor ? `bg-${categoryColor}-600` : 'bg-green-600']">
                            {{ model.forsideKategori }}
                        </span>
                        <span v-if="model.redaksjonsomtale" class="text-xs font-bold tracking-wider bg-gray-500 p-1 text-white rounded-sm mb-2">
                            Tilrettelagt av redaksjonen
                        </span>
                        <span v-if="model.fagfellevurdert" class="text-xs font-bold tracking-wider p-1 text-white rounded-sm mb-2" :class="[categoryColor ? `bg-${categoryColor}-600` : 'bg-green-600']">
                            Fagfellevurdert artikkel
                        </span>
                    </div>

                    <ContentArea v-epi-edit="'RightColumnContent'" :model="model.rightColumnContent" />

                    <article-box v-if="inEditMode" :header='`Ulike instillinger for denne siden`'>
                       <div>
                            <EpiProperty propertyName="Redaksjonsomtale" displayText="Tilrettelagt av redaksjonen" />
                            <EpiProperty propertyName="Fagfellevurdert" displayText="Fagfellevurdert" />
                            <EpiProperty propertyName="VisDatoOgForfatter" displayText="Vis publisert/forfatterinfo" />
                            <EpiProperty v-show="!this.model.imageTopRight" propertyName="ImageTopRight" displayText="Forfatterbilde" />
                            <EpiProperty propertyName="HideByline" displayText="Skjul byline" />
                        </div>
                    </article-box>

                    <!-- Metadata - dato -->
                    <article-box v-if="model.visDatoOgForfatter || inEditMode" :header='`Publisert`' :text="new Date(this.model.startPublish).toLocaleDateString('nb-NO', dateOptions)" :colorTheme="categoryColor"></article-box>
                    <!-- // Metadata - dato -->

                    <!-- Forfatter -->
                    <article-box v-if="model.visDatoOgForfatter || inEditMode" :header="(model.kategori !== undefined && model.kategori.name) === 'Bokomtaler' ? 'Bokomtaler': 'Forfatter'" :colorTheme="categoryColor">
                        <img v-epi-edit="'ImageTopRight'" v-if="authorImage" :src="authorImage" :alt="this.model.metaAuthor" class="mb-4 mt-1" />
                        <p v-if="author || inEditMode" v-epi-edit="'MetaAuthor'" class="font-bold" v-html="author"></p>

                        <div v-if="model.forfatterInfo || inEditMode" v-epi-edit="'ForfatterInfo'" v-html="model.forfatterInfo"></div>
                    </article-box>
                    <!-- // Forfatter -->

                    <!-- Utgiver og utgivelsesår -->
                    <article-box v-if="model.utgiver || model.utgivelsesAar || inEditMode" :header='`Utgiver / utgivelsesår`' :colorTheme="categoryColor">
                        <p><span v-epi-edit="'Utgiver'">{{ model.utgiver }}</span> / <span v-epi-edit="'UtgivelsesAar'">{{ model.utgivelsesAar }}</span></p>
                    </article-box>
                    <!-- // Utgiver og utgivelsesår -->

                    <!-- Main list items -->
                    <article-box v-if="model.mainListItems || inEditMode" :colorTheme="categoryColor">
                        <ul>
                            <li
                                v-for="item in model.mainListItems"
                                :key="item.id"
                            >
                                <a
                                    :href="item.url"
                                    :title="item.name"
                                    class="text-black hover:text-green-500 underline truncate mr-2"
                                >
                                    {{ item.name }}
                                </a>
                            </li>
                        </ul>
                    </article-box>
                    <!-- // Main list items -->

                    <!-- Referanse -->
                    <article-box v-if="artikkelReferanse || inEditMode" :header='articleRef' :html="artikkelReferanse" :colorTheme="categoryColor">
                        <div v-if="inEditMode">
                            <EpiProperty propertyName="ArtikkelReferanseTitle" displayText="Overskrift" />
                            <EpiProperty propertyName="ArtikkelReferanse" displayText="Tekst" />
                        </div>
                    </article-box>
                    <!-- // Referanse -->

                    <!-- Documents -->
                    <article-box v-if="documents.length > 0 || inEditMode" :header='documentsTitle' :colorTheme="categoryColor">
                        <dl>
                            <dd v-for="item in documents" :key="item.title" class="pt-2">
                                <div class="text-grey-400 hover:text-green-500">
                                    <a :href="item.url" :target="item.target" :title="item.text">
                                        <div class="flex flex-row">
                                            <div class="flex-1 text-black hover:text-green-500 underline truncate mr-2">
                                                {{item.text}}
                                            </div>
                                            <font-awesome-icon style="margin-top: 6px;" icon="file-alt" />
                                        </div>
                                    </a>
                                </div>
                            </dd>
                        </dl>
                    </article-box>
                    <!-- // Documents -->

                    <!-- Resources -->
                    <article-box v-if="resources.length > 0 || inEditMode" :header='resourcesTitle' :colorTheme="categoryColor">
                        <dl>
                            <dd v-for="item in resources" :key="item.title" class="pt-2">
                                <div class="text-grey-400 hover:text-green-500">
                                    <a :href="item.url" :target="item.target" :title="item.text">
                                        <div class="flex flex-row">
                                            <div class="flex-1 text-black hover:text-green-500 underline truncate mr-2">
                                                {{item.text}}
                                            </div>
                                            <font-awesome-icon style="margin-top: 6px;" icon="file-alt" />
                                        </div>
                                    </a>
                                </div>
                            </dd>
                        </dl>
                        <a
                            v-if="model.loginReturnUrl && model.loginButtonText"
                            :href="model.loginReturnUrl"
                            class="btn btn-green justify-center"
                        >
                            {{ model.loginButtonText }}
                        </a>
                    </article-box>
                    <!-- // Resources -->

                    <!-- Internal pages -->
                    <article-box v-if="internalPages.length > 0 || inEditMode" :header='internalPagesTitle' :colorTheme="categoryColor">
                        <dl>
                            <dd v-for="item in internalPages" :key="item.title" class="pt-2">
                                <div class="text-grey-400 hover:text-green-500">
                                    <a :href="item.url" :target="item.target" :title="item.text">
                                        <div class="flex flex-row">
                                            <div class="flex-1 text-black hover:text-green-500 underline truncate mr-2">
                                                {{item.text}}
                                            </div>
                                            <font-awesome-icon  style="margin-top: 6px;" icon="link" />
                                        </div>
                                    </a>
                                </div>
                            </dd>
                        </dl>
                    </article-box>
                    <!-- // Internal pages -->

                    <!-- External pages -->
                    <article-box v-if="externalPages.length > 0 || inEditMode" :header='externalPagesTitle' :colorTheme="categoryColor">
                        <dl>
                            <dd v-for="item in externalPages" :key="item.title" class="pt-2">
                                <div class="text-grey-400 hover:text-green-500">
                                    <a :href="item.url" :target="item.target" :title="item.text">
                                        <div class="flex flex-row">
                                            <div class="flex-1 text-black hover:text-green-500 underline truncate mr-2">
                                                {{item.text}}
                                            </div>
                                            <font-awesome-icon style="margin-top: 6px;" icon="external-link-alt" />
                                        </div>
                                    </a>
                                </div>
                            </dd>
                        </dl>
                    </article-box>
                    <!-- // External pages -->

                    <!-- Nøkkelord -->
                    <article-box v-if="model.nokkelord || inEditMode" :header='`Nøkkelord`' :html="model.nokkelord" :colorTheme="categoryColor">
                    </article-box>
                    <!-- // Nøkkelord  -->

                    <!-- PDF -->
                    <article-box v-if="model.pdfUtgave || inEditMode" :header='`PDF-utgave`' :colorTheme="categoryColor">
                        <a :href="model.pdfUtgave" title="Last ned PDF" class="underline hover:text-green-500">Last ned PDF</a>
                    </article-box>
                    <!-- // PDF -->
                </aside>
            </div>
        </div>
    </DefaultTemplate>
</template>

<script>
import Breadcrumbs from '@/Scripts/components/organisms/Breadcrumbs/breadcrumbs.vue';
import ContentArea from '@/Scripts/components/ContentArea.vue';
import EpiProperty from '@/Scripts/components/EpiProperty.vue';
import Sharing from '@/Scripts/components/atoms/Sharing/Sharing.vue';
import ImageSlider from '@/Scripts/components/atoms/ImageSlider/ImageSlider.vue';
import ArticleBox from '@/Scripts/components/atoms/ArticleBox/ArticleBox.vue';
import { mapState } from 'vuex';

export default {
    props: ['model'],
    components: {
        Breadcrumbs,
        ContentArea,
        EpiProperty,
        Sharing,
        ArticleBox,
        ImageSlider
    },
    computed: mapState({
        inEditMode: state => state.epiContext.inEditMode
    }),
    data() {
        return {
            themeColor: this.model.color ? this.model.color : 'green',
            dateOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
            authorImage: this.model.imageTopRight ? this.model.imageTopRight + '?scale=both&width=320&mode=pad&anchor=middleleft' : null,
            author: this.model.metaAuthor ? this.model.metaAuthor : 'Redaksjonen',

            artikkelReferanse: this.model.artikkelReferanse ? this.model.artikkelReferanse : false,
            articleRef: this.model.artikkelReferanseTitle ? this.model.artikkelReferanseTitle : 'Artikkelreferanser',

            internalPages: this.model.internalPages ? this.model.internalPages : [],
            internalPagesTitle: this.model.internalPagesTitle ? this.model.internalPagesTitle : 'Interne lenker',

            externalPages: this.model.externalPages ? this.model.externalPages : [],
            externalPagesTitle: this.model.externalPagesTitle ? this.model.externalPagesTitle : 'Eksterne lenker',

            documents: this.model.documents ? this.model.documents : [],
            documentsTitle: this.model.documentsTitle ? this.model.documentsTitle : 'Dokumenter',

            resources: this.model.resources ? this.model.resources : [],
            resourcesTitle: this.model.resourcesTitle ? this.model.resourcesTitle : 'Mer informasjon',

            imageParams: this.model.imageResizerParams ? 'scale=both&width=1112&maxheight=500&' + this.model.imageResizerParams : 'scale=both&width=1112&maxheight=500&'
        };
    },
    methods: {
        mapIcon(type) {
            switch (type) {
                case 'pdf':
                    return 'file-pdf';
                default:
                    return 'file-alt';
            }
        }
    }
};
</script>
